/**
 * /app/cupones — Gestión de cupones de descuento (Admin UI).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import { Plus, Ticket } from "lucide-react";
import { Loading } from "@/components/ds";
import { CrudShellHeader, CrudEmptyState } from "@/components/ds/CrudShell";
import { ClinicalPageShell } from "@/components/clinical";
import { DiscountCouponsTour } from "@/components/ProductTour";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { apiGetJson, apiSendJson, apiDelete } from "@/lib/sm-api";
import { toast } from "sonner";
import { CouponWizard, CouponListCard } from "@/components/discounts/CouponWizard";
import type { CouponFormData } from "@/components/discounts/CouponWizard";
import { useAppDialogs } from "@/components/ui/app-dialogs";

export const Route = createFileRoute("/app/cupones")({
  component: CuponesPage,
  head: () => ({ meta: [{ title: "Cupones — Structura Market" }] }),
});

function CuponesPage() {
  const tenantId = useWorkspaceTenantId();
  const [showWizard, setShowWizard] = useState(false);
  const { confirm } = useAppDialogs();
  const [editing, setEditing] = useState<CouponFormData | null>(null);
  const [coupons, setCoupons] = useState<CouponFormData[]>([]);
  const [loading, setLoading] = useState(true);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLock(null);
    try {
      const res = await apiGetJson<{ coupons: CouponFormData[] }>("/api/coupons", tenantId);
      setCoupons(res.coupons ?? []);
    } catch (e: unknown) {
      const err = e as Error & { planLock?: PlanApiErrorPayload };
      if (err.planLock) setLock(err.planLock);
      else toast.error("Error al cargar cupones");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  async function handleSave(form: CouponFormData) {
    try {
      await apiSendJson(form.id ? `/api/coupons?id=${form.id}` : "/api/coupons", "POST", form, tenantId);
      toast.success(form.id ? "Cupón actualizado" : "Cupón creado");
      await load();
      setShowWizard(false);
      setEditing(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al guardar el cupón");
    }
  }

  async function handleToggle(id: string) {
    try {
      await apiSendJson(`/api/coupons?id=${id}&action=toggle`, "POST", {}, tenantId);
      await load();
    } catch {
      toast.error("Error al cambiar estado");
    }
  }

  async function handleDelete(id: string) {
    if (!(await confirm({ title: "¿Eliminar este cupón?", destructive: true, confirmLabel: "Eliminar" }))) return;
    try {
      await apiDelete(`/api/coupons?id=${id}`, tenantId);
      toast.success("Cupón eliminado");
      await load();
    } catch {
      toast.error("Error al eliminar");
    }
  }

  if (lock) return <FeatureLockedState payload={lock} />;

  return (
    <div>
      <DiscountCouponsTour />
      <CrudShellHeader
        title="Cupones de descuento"
        description="Creá cupones de % o monto fijo con límite de uso y fecha de vencimiento"
        backHref="/app/descuentos"
        backLabel="Volver a Ventas"
        count={coupons.length}
        action={<button onClick={() => setShowWizard(true)} className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"><Plus size={16} />Nuevo cupón</button>}
      />
      <ClinicalPageShell offset="none">
        {loading ? (
        <Loading variant="list" rows={3} />
      ) : (
        <div className="max-w-2xl mx-auto space-y-6 p-4">
          {!showWizard && !editing && (
            <button
              onClick={() => setShowWizard(true)}
              data-tour="cupones-nuevo"
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-2xl py-4 text-sm text-gray-500 hover:border-blue-400 hover:text-blue-500 transition"
            >
              <Plus size={18} /> Nuevo cupón
            </button>
          )}

          {(showWizard || editing) && (
            <CouponWizard
              tenantId={tenantId ?? ""}
              initial={editing ?? undefined}
              onSave={handleSave}
              onCancel={() => { setShowWizard(false); setEditing(null); }}
            />
          )}

          {!showWizard && !editing && (
            <div className="space-y-3">
              {coupons.length === 0 && <CrudEmptyState icon={<Ticket className="h-8 w-8" />} title="No tenés cupones aún" description="Creá tu primer cupón con el botón de arriba." />}
              {coupons.map((c) => (
                <CouponListCard
                  key={c.id}
                  coupon={c}
                  onEdit={() => setEditing(c)}
                  onToggle={() => handleToggle(c.id!)}
                  onDelete={() => handleDelete(c.id!)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </ClinicalPageShell>
    </div>
  );
}
